/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import React, { Component } from 'react';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import Button from '@react/react-spectrum/Button';
import DropdownButton from '@react/react-spectrum/DropdownButton';
import Tooltip from '@react/react-spectrum/Tooltip';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';
import { MenuItem } from '@react/react-spectrum/Menu';
import Wait from '@react/react-spectrum/Wait';
import CloseCircle from '@react/react-spectrum/Icon/CloseCircle';
import './appActionBar.scss';
import PropTypes from 'prop-types';
import uuid from 'uuid';
import { ACTION, ANALYTICS } from '../../../../../utils/enums';
import Config from '../../../../../stores/config';
import App from '../../../../../stores/app/app';
import Icons from '../../../../../utils/iconMapping';
import { interpretKey } from '../../../../../utils/eventUtil';
import { PDPAnalytics } from '../../../../../analytics';
import { ACTION_BAR_SECTION, ACTION_ID } from '../../../../../utils/modelMapper/appModelMapper/appModelConstants';
import TranslationUtil from '../../../../../utils/translation';


export default @observer class AppActionBar extends Component {
    static propTypes = {
        /*
         * app object for progress config,
         * actionList (specific to app) and onAppAction callback
         */
        app: PropTypes.shape(App).isRequired,

        /*
         * the source that is rendering
         * the component (pdp/appTile/information dialog etc)
         */
        source: PropTypes.string.isRequired,

        /*
         * Action list specific to the section i.e.
         * all-apps/pdp/photo plans etc.
         */
        applicableActionList: PropTypes.arrayOf(PropTypes.shape(
            { actionId: PropTypes.string.isRequired, actionType: PropTypes.object.isRequired }
        )),

    };

    static defaultProps = {
        applicableActionList: [],
    }

    @computed
    get actionMap() {
        const actions = {
            buttonList: [],
            dropDownList: [],
        };
        const {
            app: { actionList, progress },
            applicableActionList,
        } = this.props;
        if (progress) {
            return actions;
        }
        /* take intersection of app specific actions and view specific
        actions to find button and dropdown config */
        applicableActionList.map((viewSpecificAction) => {
            let action = actionList.filter(
                appSpecificAction => appSpecificAction.actionId === viewSpecificAction.actionId
            );
            if (action.length > 0) {
                action = {
                    ...action[0],
                    title: viewSpecificAction.title,
                    actionType: viewSpecificAction.actionType,
                };
                switch (action.actionType.component.toLowerCase()) {
                case ACTION.COMPONENT.BUTTON:
                    actions.buttonList.push(action);
                    break;
                case ACTION.COMPONENT.DROPDOWN:
                    actions.dropDownList.push(action);
                    break;
                default:
                }
            }
            return null;
        });
        return actions;
    }

    getButtonIcon(iconType) {
        const { Alert } = Icons;
        if (iconType === 'error') {
            return <Alert variant="error" size="S" />;
        }
        const CTAIcon = Icons[iconType];
        if (CTAIcon) {
            return <CTAIcon size="S" />;
        }
        return null;
    }

    getButtonClass(iconType) {
        switch (iconType) {
        case 'error':
            return 'appAction-error'; // NOTE: update element classname in new-relic also (if applicable)
        default:
            return '';
        }
    }

    sendAnalytics(actionConfig, guid, uiViewType) {
        const { source } = this.props;
        switch (source) {
        case 'pdp':
            PDPAnalytics.handleActionBarAnalytics(
                actionConfig,
                uiViewType,
                ACTION_BAR_SECTION,
                guid
            );
            break;
        default:
        }
    }

    renderDropdownButton(app) {
        const {
            onAppAction,
        } = app;
        return (
            <DropdownButton
                onKeyDown={interpretKey({
                    onEnter: [null, true],
                })}
                tabIndex={0}
                aria-label={TranslationUtil.getMessage('ccx.appTile.platformIcons.moreActions.tooltip')}
                role="button"
                className="appAction-dropDownButton">
                {this.actionMap.dropDownList.map((dropDown) => {
                    const { localizedText, actionId, actionType = {} } = dropDown;
                    const menuItem = (
                        <MenuItem
                            key={localizedText}
                            className={classnames('appAction-dropDownItem', { errorText: actionType.textType === 'error' })}
                            onClick={() => {
                                const guid = uuid.v4();
                                onAppAction(
                                    actionId,
                                    app,
                                    {
                                        ...this.data,
                                        guid,
                                        inAppSource: ANALYTICS.PDP_forUrlParam,
                                    }
                                );
                                this.sendAnalytics(dropDown, guid, 'Operations Menu');
                            }}
                            value={localizedText}
                            aria-label={localizedText}
                            role="button">
                            {localizedText}
                        </MenuItem>
                    );
                    return menuItem;
                })}
            </DropdownButton>
        );
    }

    renderDropdownList(app) {
        if (Config.isNonDownloadableSurface) {
            // This is a touch device, therefore no tooltip
            return this.renderDropdownButton(app);
        }
        return (
            <OverlayTrigger placement="bottom" trigger={['hover']} delayShow={1000}>
                {this.renderDropdownButton(app)}
                <Tooltip>
                    {TranslationUtil.getMessage('ccx.appTile.platformIcons.moreActions.tooltip')}
                </Tooltip>
            </OverlayTrigger>
        );
    }

    renderCTAs = (app) => {
        const {
            onAppAction,
        } = app;
        return (
            <div className="app-related-actions">
                {this.actionMap.buttonList.map((button) => {
                    const {
                        actionType, localizedText, actionId, title,
                    } = button;
                    const {
                        icon, iconOnly, quiet, variant,
                    } = actionType;
                    const errorIcon = this.getButtonIcon(icon);
                    // NOTE: update element classname in new-relic also (if applicable)
                    const actionButton = (
                        <Button
                            className={`appAction-button ${this.getButtonClass(icon)}`}
                            onClick={() => {
                                const guid = uuid.v4();
                                onAppAction(
                                    actionId,
                                    app,
                                    {
                                        ...this.data,
                                        guid,
                                        inAppSource: ANALYTICS.PDP_forUrlParam,
                                    }
                                );
                                this.sendAnalytics(button, guid);
                            }}
                            onKeyDown={interpretKey({
                                onEnter: [null, true],
                            })}
                            quiet={quiet}
                            variant={variant}
                            disabled={actionType.isDisabled}
                            label={iconOnly ? null : localizedText}
                            key={localizedText}
                            icon={errorIcon}
                            tabIndex={0}
                            title={title}
                            aria-label={localizedText}
                            role="button" />
                    );
                    return actionButton;
                })}

                { this.actionMap.dropDownList
                && this.actionMap.dropDownList.length > 0
                && this.renderDropdownList(app)}
            </div>
        );
    };

    renderProgress = (app) => {
        const {
            progress = null,
            onAppAction,
        } = app;
        const { localizedMessageText } = progress;
        let progressContent;
        if (progress.isWaiting) {
            progressContent = (
                <div className="appAction-isWaiting-container">
                    <div className="appAction-isWaiting-progress">
                        {localizedMessageText ? `${localizedMessageText}...` : ''}
                    </div>
                </div>
            );
        } else {
            progressContent = (
                <div className="appAction-progress-container">
                    <div className="appAction-progress">
                        <Wait className="icon" indeterminate={false} value={progress.percentage * 100} size="S" />
                        <div className="appAction-progress-bar-text">
                            <div className="appAction-progress-bar-left-text">
                                {localizedMessageText ? `${localizedMessageText}...` : ''}
                            </div>
                            <div className="appAction-progress-bar-right-text">
                                ({TranslationUtil.getMessage(
                                    'ccx.progress.percentage',
                                    { percentage: Math.round(progress.percentage * 100) }
                                )})
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        const progressCancelCallback = () => {
            const actionId = ACTION_ID.PROGRESS_CANCEL;
            const guid = uuid.v4();
            onAppAction(
                actionId,
                app,
                { ...this.data, guid }
            );
            this.sendAnalytics(
                { actionId, progressCount: progress.percentage * 100 },
                guid
            );
        };
        return (
            <div className="progress-container">
                <div className="progress-content">
                    { progressContent }
                </div>
                <div className="progress-cancel">
                    {
                        /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
                        progress.isCancellable && (
                            <span
                                onClick={progressCancelCallback}
                                onKeyDown={interpretKey({
                                    onEnter: [progressCancelCallback, true],
                                })}
                                tabIndex={0}>
                                <CloseCircle size="S" />
                            </span>
                        )
                        /* eslint-enable jsx-a11y/no-noninteractive-tabindex */
                    }
                </div>
            </div>
        );
    };

    render() {
        const {
            app,
            source,
        } = this.props;
        this.data = { source };
        return (
            app.progress ? this.renderProgress(app)
                : this.renderCTAs(app)
        );
    }
}
